import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { JsonLd } from '@/components/base/JsonLd';
import { articles, categories, testimonials, faqItems } from '@/mocks/news';

function buildArticleSchema(article: typeof articles[0], categoryName: string, categorySlug: string, articleUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '黑料在线',
    },
    articleSection: categoryName,
    url: articleUrl,
  };
}

function buildBreadcrumb(article: typeof articles[0], categoryName: string, categorySlug: string, articleUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://baiducc.cn/' },
      { '@type': 'ListItem', position: 2, name: categoryName, item: `http://baiducc.cn/category/${categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
    ],
  };
}

function buildFAQSchema(faqs: typeof faqItems) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.slug === slug) || fallbackArticle;
  const category = article ? categories.find((c) => c.slug === article.category) : null;

  const articleImageMap: Record<string, string> = {
    '1': 'https://readdy.ai/api/search-image?query=Dramatic%20news%20scene%20with%20warm%20lighting%20and%20mysterious%20atmosphere%2C%20journalism%20concept%2C%20editorial%20photography%2C%20dark%20amber%20and%20charcoal%20tones%2C%20breaking%20news%20mood%2C%20professional%20composition%2C%20wide%20format&width=1200&height=600&seq=art-detail-01&orientation=landscape',
    '2': 'https://readdy.ai/api/search-image?query=Gaming%20streaming%20setup%20in%20darkness%20with%20dramatic%20red%20neon%20lighting%2C%20editorial%20photography%2C%20mysterious%20atmosphere%2C%20warm%20dark%20tones%2C%20professional%20composition%2C%20wide%20format&width=1200&height=600&seq=art-detail-02&orientation=landscape',
    '3': 'https://readdy.ai/api/search-image?query=Film%20set%20with%20director%20chair%20in%20spotlight%2C%20dramatic%20cinematic%20lighting%2C%20editorial%20photography%2C%20warm%20golden%20tones%2C%20behind%20the%20scenes%2C%20wide%20format&width=1200&height=600&seq=art-detail-03&orientation=landscape',
    '4': 'https://readdy.ai/api/search-image?query=Television%20studio%20stage%20with%20dramatic%20spotlights%2C%20reality%20show%20set%2C%20editorial%20photography%2C%20dark%20atmosphere%2C%20warm%20golden%20tones%2C%20wide%20format&width=1200&height=600&seq=art-detail-04&orientation=landscape',
    '5': 'https://readdy.ai/api/search-image?query=Cosmetic%20products%20on%20dark%20surface%20under%20dramatic%20lighting%2C%20mysterious%20laboratory%20atmosphere%2C%20editorial%20photography%2C%20warm%20amber%20tones%2C%20investigative%20mood%2C%20wide%20format&width=1200&height=600&seq=art-detail-05&orientation=landscape',
    '6': 'https://readdy.ai/api/search-image?query=Two%20spotlights%20facing%20each%20other%20on%20dark%20stage%2C%20musical%20instruments%2C%20dramatic%20confrontation%2C%20editorial%20photography%2C%20warm%20golden%20tones%2C%20wide%20format&width=1200&height=600&seq=art-detail-06&orientation=landscape',
    '7': 'https://readdy.ai/api/search-image?query=Smartphone%20screen%20displaying%20messages%20in%20dark%20environment%2C%20dramatic%20moody%20lighting%2C%20editorial%20photography%2C%20warm%20amber%20tones%2C%20wide%20format&width=1200&height=600&seq=art-detail-07&orientation=landscape',
    '8': 'https://readdy.ai/api/search-image?query=Empty%20modern%20office%20with%20dim%20lighting%2C%20abandoned%20desks%2C%20corporate%20decline%2C%20editorial%20photography%2C%20warm%20dark%20tones%2C%20wide%20format&width=1200&height=600&seq=art-detail-08&orientation=landscape',
    '9': 'https://readdy.ai/api/search-image?query=Luxury%20cosmetic%20products%20under%20magnifying%20glass%2C%20investigative%20atmosphere%2C%20editorial%20photography%2C%20warm%20golden%20tones%2C%20wide%20format&width=1200&height=600&seq=art-detail-09&orientation=landscape',
    '10': 'https://readdy.ai/api/search-image?query=Gaming%20setup%20with%20multiple%20monitors%20in%20dark%20room%2C%20dramatic%20neon%20lighting%2C%20esports%20atmosphere%2C%20editorial%20photography%2C%20wide%20format&width=1200&height=600&seq=art-detail-10&orientation=landscape',
    '11': 'https://readdy.ai/api/search-image?query=Smartphone%20camera%20lens%20with%20city%20street%20reflection%2C%20privacy%20concept%2C%20dramatic%20lighting%2C%20urban%20night%2C%20wide%20format&width=1200&height=600&seq=art-detail-11&orientation=landscape',
    '12': 'https://readdy.ai/api/search-image?query=Fashion%20design%20sketches%20on%20dark%20table%2C%20creative%20studio%2C%20dramatic%20warm%20lighting%2C%20editorial%20photography%2C%20wide%20format&width=1200&height=600&seq=art-detail-12&orientation=landscape',
  };

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-2">文章不存在</h1>
            <Link to="/" className="text-accent-600 hover:underline text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const randomTestimonial = testimonials[Math.floor(Math.random() * testimonials.length)];

  return (
    <>
      <JsonLd data={buildArticleSchema(article, category?.name || '', article.category, `http://baiducc.cn/article/${article.slug}`)} />
      <JsonLd data={buildBreadcrumb(article, category?.name || '', article.category, `http://baiducc.cn/article/${article.slug}`)} />
      <JsonLd data={buildFAQSchema(faqItems)} />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-4">
          <div className="max-w-4xl mx-auto">
            <nav aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <li><Link to="/" className="hover:text-foreground-900 transition-colors">首页</Link></li>
                <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                {category && (
                  <>
                    <li><Link to={`/category/${category.slug}`} className="hover:text-foreground-900 transition-colors">{category.name}</Link></li>
                    <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                  </>
                )}
                <li className="text-foreground-900 font-medium truncate max-w-[200px]">{article.title}</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* Article content */}
        <article className="px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-4xl mx-auto">
            {/* Featured image */}
            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src={articleImageMap[article.id] || articleImageMap['1']}
                alt={article.title}
                className="w-full aspect-[2/1] object-cover object-top"
                title={`${article.title} - 黑料在线`}
              />
            </div>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="text-xs font-medium bg-accent-100 text-accent-700 px-3 py-1 rounded-full hover:bg-accent-200 transition-colors"
                >
                  {category.name}
                </Link>
              )}
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-200/70 px-2 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="text-xl md:text-3xl font-black font-heading text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            {/* Author and date */}
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}
              </span>
            </div>

            {/* Content */}
            <div
              className="prose prose-foreground max-w-none mb-12 text-sm md:text-base leading-relaxed text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Testimonial section */}
            <div className="relative rounded-2xl bg-foreground-900 overflow-hidden mb-14">
              <div className="grid grid-cols-1 md:grid-cols-5">
                <div className="md:col-span-2 relative min-h-[200px] md:min-h-[320px] -mb-1 md:-mr-8 md:-my-0 z-10">
                  <img
                    src="https://readdy.ai/api/search-image?query=Dramatic%20portrait%20silhouette%20with%20warm%20golden%20rim%20lighting%2C%20mysterious%20anonymous%20figure%2C%20editorial%20portrait%20photography%2C%20dark%20atmosphere%2C%20cinematic%20mood%2C%20soft%20backlighting%2C%20professional%20composition%2C%20deep%20warm%20tones&width=600&height=800&seq=testimonial-img&orientation=portrait"
                    alt="用户评价"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="md:col-span-3 flex flex-col justify-center p-6 md:p-10">
                  <div className="text-accent-400 text-5xl font-serif mb-4">&ldquo;</div>
                  <blockquote className="text-sm md:text-lg font-medium text-background-50 leading-relaxed mb-4">
                    {randomTestimonial.quote}
                  </blockquote>
                  <div className="text-xs text-foreground-400">
                    <span className="font-semibold text-background-50">{randomTestimonial.author}</span>
                    <span className="mx-2">·</span>
                    <span>{randomTestimonial.role}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Author / Platform note */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-14">
              <div className="md:col-span-2">
                <span className="text-xs font-medium text-foreground-500 border border-background-300 px-3 py-1 rounded-full mb-3 inline-block">
                  平台声明
                </span>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mt-3 mb-4">
                  关于黑料在线
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  黑料在线致力于为读者提供真实、客观的网红八卦资讯。我们拥有专业的采编团队，所有内容均经过多方核实后发布。我们严格遵守新闻伦理，保护爆料人隐私，坚持做有态度的资讯平台。
                </p>
                <Link to="/about" className="text-sm text-accent-600 font-medium hover:underline inline-flex items-center gap-1">
                  了解更多
                  <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="md:col-span-3 relative">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl overflow-hidden aspect-[3/4] translate-y-4">
                    <img
                      src="https://readdy.ai/api/search-image?query=Journalism%20newsroom%20with%20dramatic%20warm%20lighting%2C%20editorial%20photography%20style%2C%20dark%20amber%20and%20charcoal%20tones%2C%20professional%20atmosphere%2C%20typewriter%20on%20desk%2C%20mysterious%20mood%2C%20investigative%20journalism%20theme&width=400&height=560&seq=about-img-01&orientation=portrait"
                      alt="黑料在线编辑部"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="rounded-xl overflow-hidden aspect-[3/4] -translate-y-4">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20media%20team%20workspace%20with%20dramatic%20lighting%2C%20editorial%20photography%2C%20warm%20golden%20and%20dark%20tones%2C%20news%20production%20atmosphere%2C%20professional%20composition%2C%20mysterious%20mood&width=400&height=560&seq=about-img-02&orientation=portrait"
                      alt="黑料在线团队"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
                <div className="absolute top-0 right-0 flex gap-6">
                  <div className="text-right">
                    <div className="text-3xl md:text-4xl font-black font-heading text-foreground-950">5000+</div>
                    <div className="text-xs text-foreground-500">累计爆料</div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl md:text-4xl font-black font-heading text-foreground-950">2000+</div>
                    <div className="text-xs text-foreground-500">覆盖网红</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Related articles */}
            {relatedArticles.length > 0 && (
              <div className="mb-14">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  相关阅读
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer"
                    >
                      <img
                        src={articleImageMap[rel.id] || articleImageMap['1']}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={`${rel.title} - 黑料在线`}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/20 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="text-sm font-semibold font-heading text-background-50 line-clamp-2">
                          {rel.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ */}
            <div className="bg-background-100 rounded-2xl p-6 md:p-10 mb-10">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqItems.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl">
                    <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-foreground-900 list-none">
                      {faq.question}
                      <i className="ri-add-line group-open:hidden text-foreground-500"></i>
                      <i className="ri-subtract-line hidden group-open:block text-foreground-500"></i>
                    </summary>
                    <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}